<script setup lang="ts">
// ナビゲーター: 合成結果の縮小版と表示範囲。クリック / ドラッグで表示位置、スライダーで倍率。
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useUiStore, ZOOM_MIN, ZOOM_MAX } from '@/stores/uiStore'
import { readCompositeCached } from '@/app/engine'

const docStore = useDocumentStore()
const ui = useUiStore()
const canvas = ref<HTMLCanvasElement | null>(null)
const W = 240
const size = computed(() => {
  const d = docStore.doc
  if (!d) return { w: W, h: 100, s: 1 }
  const s = Math.min(W / d.meta.width, 140 / d.meta.height)
  return { w: Math.max(1, Math.round(d.meta.width * s)), h: Math.max(1, Math.round(d.meta.height * s)), s }
})
let thumb: ImageBitmap | null = null
let timer: number | null = null
async function rebuildThumb() {
  const d = docStore.doc
  if (!d) {
    thumb = null
    draw()
    return
  }
  const c = readCompositeCached()
  if (!c) return
  const img = new ImageData(new Uint8ClampedArray(c.rgba.buffer, c.rgba.byteOffset, c.rgba.byteLength) as unknown as Uint8ClampedArray<ArrayBuffer>, c.width, c.height)
  try {
    thumb?.close()
    thumb = await createImageBitmap(img, { resizeWidth: size.value.w, resizeHeight: size.value.h, resizeQuality: 'medium' })
  } catch {
    thumb = null
  }
  draw()
}
function scheduleThumb() {
  if (timer !== null) clearTimeout(timer)
  timer = window.setTimeout(() => {
    timer = null
    void rebuildThumb()
  }, 150)
}
function draw() {
  const c = canvas.value
  if (!c) return
  const { w, h, s } = size.value
  c.width = w
  c.height = h
  const ctx = c.getContext('2d')!
  ctx.clearRect(0, 0, w, h)
  if (thumb) ctx.drawImage(thumb, 0, 0, w, h)
  const d = docStore.doc
  if (!d) return
  // 表示範囲 (文書座標) を矩形で
  const tl = ui.cssToDoc({ x: 0, y: 0 }), br = ui.cssToDoc({ x: ui.viewportSize.w, y: ui.viewportSize.h })
  ctx.strokeStyle = '#e5484d'
  ctx.lineWidth = 1.5
  ctx.strokeRect(tl.x * s + 0.5, tl.y * s + 0.5, (br.x - tl.x) * s, (br.y - tl.y) * s)
}
function moveTo(e: PointerEvent) {
  const c = canvas.value
  const d = docStore.doc
  if (!c || !d) return
  const r = c.getBoundingClientRect()
  const px = ((e.clientX - r.left) / r.width) * d.meta.width
  const py = ((e.clientY - r.top) / r.height) * d.meta.height
  const z = ui.view.zoom
  ui.view = { zoom: z, pan: { x: ui.viewportSize.w / 2 - px * z, y: ui.viewportSize.h / 2 - py * z } }
}
let dragging = false
function onDown(e: PointerEvent) {
  dragging = true
  ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
  moveTo(e)
}
function onMove(e: PointerEvent) {
  if (dragging) moveTo(e)
}
function onUp() {
  dragging = false
}
const zoomPct = computed(() => Math.round(ui.view.zoom * 1000) / 10)
function setZoom(v: number) {
  const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v / 100))
  ui.setZoomAt(z, { x: ui.viewportSize.w / 2, y: ui.viewportSize.h / 2 })
}
// 対数スライダー
const slider = computed(() => Math.log2(ui.view.zoom))
onMounted(() => {
  void rebuildThumb()
})
watch(() => [docStore.historyVersion, docStore.sessionVersion], scheduleThumb)
watch(() => [ui.view.zoom, ui.view.pan.x, ui.view.pan.y, ui.viewportSize.w, ui.viewportSize.h], draw)
onBeforeUnmount(() => {
  if (timer !== null) clearTimeout(timer)
  thumb?.close()
})
</script>

<template>
  <div class="panel nav">
    <div class="panel-title">ナビゲーター</div>
    <div class="body">
      <canvas ref="canvas" class="thumb" :style="{ width: size.w + 'px', height: size.h + 'px' }" @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointercancel="onUp" />
      <div class="row zoom">
        <input type="range" :min="Math.log2(ZOOM_MIN)" :max="Math.log2(ZOOM_MAX)" step="0.05" :value="slider" @input="setZoom(Math.pow(2, Number(($event.target as HTMLInputElement).value)) * 100)" />
        <span class="mono pct">{{ zoomPct }}%</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.nav {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.body {
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}
.thumb {
  background: repeating-conic-gradient(var(--checker-a) 0 25%, var(--checker-b) 0 50%) 0 0 / 12px 12px;
  border: 1px solid var(--line-strong);
  cursor: crosshair;
  touch-action: none;
}
.zoom {
  width: 100%;
}
.zoom input {
  flex: 1;
}
.pct {
  min-width: 52px;
  text-align: right;
}
</style>
